import {toast} from 'react-toastify'
import {create} from 'zustand'
import {generateFormLogic} from '../../../../../../_metronic/helpers/generate-form-logic'
import {
  FormAction,
  FormInput,
  FormProps,
  SelectData,
} from '../../../../../interfaces/form-action-interfaces'
import {FormattedMessage} from 'react-intl'
import {DivisionSingleResultInterface} from '../interfaces/division-single-result.interface'
import {
  GetAllDivisionParam,
  GetAllDivision,
  DeleteDivision,
  SubmitDivision,
  UpdateDivision,
  GetDashboardDivisionTargetAndRealization,
} from '../providers/division.provider'
import {dateTimeFormatter} from '../../../../../../_metronic/helpers/datetime-formatter'
import {DivisionItem, DivisionResultInterface} from '../interfaces/division-result.inteface'
import {DashboardDivisionTargetAndRealizationResult} from '../interfaces/dashboard-division-target-and-realization-result.interface'
import {end} from '@popperjs/core'

interface DivisionState {
  divisionLoading: boolean
  divisionItems: DivisionItem[]
  divisionSelectDatas: SelectData[]
  divisionModel?: DivisionResultInterface
  getDivisions: (params: GetAllDivisionParam) => void
  onDelete: (id: string, params: GetAllDivisionParam) => void

  dashboardDivisionIncomeLoading: boolean
  dashboardDivisionIncome: DashboardDivisionTargetAndRealizationResult
  getDashboardDivisionIncome: (
    startDate: string,
    endDate: string,
    periodeId: string,
    departmentType: string
  ) => Promise<void>

  dashboardDivisionExpenseLoading: boolean
  dashboardDivisionExpense: DashboardDivisionTargetAndRealizationResult
  getDashboardDivisionExpense: (
    startDate: string,
    endDate: string,
    periodeId: string,
    departmentType: string
  ) => Promise<void>
}

export const useDivisionStore = create<DivisionState>((set, get) => ({
  divisionLoading: false,
  divisionItems: [],
  divisionSelectDatas: [],
  divisionModel: undefined,
  getDivisions: async (params: GetAllDivisionParam) => {
    set({divisionLoading: true})
    const response = await GetAllDivision(params)
    set({divisionModel: response})
    if (response.status) {
      if (response.data.items.length > 0) {
        let transformDatas: SelectData[] = []
        for (const item of response.data.items) {
          transformDatas.push({
            label: item.division_name,
            value: item.id,
          })
        }
        set({
          divisionItems: response.data.items,
          divisionSelectDatas: transformDatas,
        })
      } else {
        set({
          divisionItems: [],
          divisionSelectDatas: [],
        })
      }
    } else {
      set({
        divisionItems: [],
        divisionSelectDatas: [],
      })
    }
    set({divisionLoading: false})
  },
  onDelete: async (id: string, params: GetAllDivisionParam) => {
    const confirm = window.confirm('Apakah anda yakin ingin menghapus data ini?')
    if (confirm === false) return
    set({divisionLoading: true})
    const response = await DeleteDivision(id)
    if (response.status) {
      toast.success(response.message)
    } else {
      toast.error(response.message)
    }
    get().getDivisions(params)
    set({divisionLoading: false})
  },
  getDashboardDivisionIncome: async (
    startDate: string,
    endDate: string,
    periodeId: string,
    departmentType: string
  ) => {
    set({dashboardDivisionIncomeLoading: true})
    const results = await GetDashboardDivisionTargetAndRealization(
      startDate,
      endDate,
      periodeId,
      departmentType
    )
    set({dashboardDivisionIncomeLoading: false, dashboardDivisionIncome: results})
  },
  dashboardDivisionIncome: undefined,
  dashboardDivisionIncomeLoading: false,

  getDashboardDivisionExpense: async (
    startDate: string,
    endDate: string,
    periodeId: string,
    departmentType: string
  ) => {
    set({dashboardDivisionExpenseLoading: true})
    const results = await GetDashboardDivisionTargetAndRealization(
      startDate,
      endDate,
      periodeId,
      departmentType
    )
    set({dashboardDivisionExpenseLoading: false, dashboardDivisionExpense: results})
  },
  dashboardDivisionExpense: undefined,
  dashboardDivisionExpenseLoading: false,
}))

const formInitialState = {
  loading: false,
  forms: [],
  formParam: undefined,
  formLoading: undefined,
  submitDone: undefined,
}

export interface DivisionFormState {
  field?: DivisionItem
  setField?: (item: DivisionItem) => void
  forms?: FormInput[]
  setForms?: (index: number, value: any) => void
  generateForms: (formParam: FormProps) => void
  setFormSelectData: (index: number, selectData: SelectData[]) => void
  formParam: FormProps
  reset: () => void
  formLoading?: boolean
  submitDone?: boolean
  onSubmit: () => void
}

export const useDivisionFormStore = create<DivisionFormState>((set, get) => ({
  field: undefined,
  setField: (item: DivisionItem) => {
    set({field: item})
  },
  formParam: undefined,
  setForms: (index: number, value: any) => {
    const forms = [...get().forms]
    forms[index] = {
      ...forms[index],
      value: value,
    }
    set({forms: forms})
  },
  generateForms: (formParam: FormProps) => {
    set({formParam: formParam})
    let forms: FormInput[] = []
    forms = [
      {
        id: 'division_name',
        title: 'Division',
        placeholder: 'Division...',
        type: 'text',
        name: 'division_name',
        value: get().field?.division_name ?? '',
        disabled: formParam.action === FormAction.VIEW ? true : false,
        required: true,
      },
      {
        id: 'department_id',
        title: 'Department',
        placeholder: 'Department...',
        type: 'select-with-text',
        name: 'department_id',
        value: {
          label: get().field?.department_name ?? '',
          value: get().field?.department_id ?? '',
        },
        selectData: [],
        disabled: formParam.action === FormAction.VIEW ? true : false,
        required: true,
      },
      {
        id: 'user_id',
        title: 'User',
        placeholder: 'User...',
        type: 'select-with-text',
        name: 'user_id',
        value: {
          value: get().field?.user_id ?? '',
          label: get().field?.user_full_name ?? '',
        },
        selectData: [],
        disabled: formParam.action === FormAction.VIEW ? true : false,
        required: true,
      },
    ]
    set({forms: forms})
  },
  reset: () => {
    set(formInitialState)
    return
  },
  onSubmit: async () => {
    let form = {}

    // check required field
    let requiredError = false
    for (const item of get().forms) {
      console.log(item.name, item.value)
      if (item.required === true && (item.value === '' || item.value?.value === '')) {
        toast.error(`${item.title} is required`)
        requiredError = true
      }
    }

    if (requiredError === true) return

    for (const item of get().forms) {
      form = {
        ...form,
        [item.name]: generateFormLogic(item.type, item.value),
      }
    }

    let res: DivisionSingleResultInterface = {}
    if (get().formParam.action === FormAction.CREATE) res = await SubmitDivision(form)
    if (get().formParam.action === FormAction.UPDATE)
      res = await UpdateDivision(get().formParam?.id, form)

    console.log(res)
    if (res.status) {
      toast.success(res.message)
      set({submitDone: true, formLoading: false, field: undefined})
      return
    } else {
      toast.error(res.error)
      set({submitDone: false, formLoading: false})
      return
    }
  },
  setFormSelectData: (index: number, selectData: SelectData[]) => {
    const forms = [...get().forms]
    forms[index] = {
      ...forms[index],
      selectData: selectData,
    }
    set({forms: forms})
  },
}))
